/**
 * IndexedDB wrapper using idb-keyval for simple key-value storage
 * with support for schema migrations and typed access
 */

import { get, set, del, keys, clear, createStore } from 'idb-keyval';
import { z } from 'zod';
import type {
  Recipe,
  CookingSession,
  CookingHistoryEntry,
  ShoppingListItem,
  PantryItem,
  MealPlanEntry,
  AppSettings,
  CookbookExport,
} from '@/types';
import { SCHEMA_VERSION } from '@/types';

// Zod schemas for import validation
const IngredientSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  amount: z.number().min(0),
  unit: z.string(),
  notes: z.string().optional(),
  optional: z.boolean().optional(),
  group: z.string().optional(),
});

const RecipeStepSchema = z.object({
  id: z.string(),
  order: z.number().min(0),
  instruction: z.string().min(1),
  timerDuration: z.number().optional(),
  timerLabel: z.string().optional(),
  notes: z.string().optional(),
  imageUrl: z.string().optional(),
});

const RecipeImportSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  description: z.string(),
  imageUrl: z.string().optional(),
  prepTime: z.number().min(0),
  cookTime: z.number().min(0),
  totalTime: z.number().min(0),
  servings: z.number().min(1),
  difficulty: z.enum(['easy', 'medium', 'hard', 'expert']),
  cuisine: z.string(),
  tags: z.array(z.string()),
  dietaryTags: z.array(z.string()),
  ingredients: z.array(IngredientSchema).min(1),
  steps: z.array(RecipeStepSchema).min(1),
  notes: z.string().optional(),
  source: z.string().optional(),
  isFavorite: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastCookedAt: z.string().optional(),
  cookCount: z.number().min(0),
  schemaVersion: z.number().optional(),
});

const CookingHistoryEntrySchema = z.object({
  id: z.string(),
  recipeId: z.string(),
  recipeName: z.string(),
  cookedAt: z.string(),
  notes: z.string().optional(),
  rating: z.number().min(1).max(5).optional(),
  duration: z.number().optional(),
});

const MealPlanEntrySchema = z.object({
  id: z.string(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  mealType: z.enum(['breakfast', 'lunch', 'dinner', 'snack']),
  recipeId: z.string().optional(),
  customMealName: z.string().optional(),
  servings: z.number().min(1),
  notes: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const ShoppingListItemSchema = z.object({
  id: z.string(),
  ingredientName: z.string().min(1),
  amount: z.number().min(0),
  unit: z.string(),
  recipeId: z.string(),
  recipeName: z.string(),
  checked: z.boolean(),
});

const PantryItemSchema = z.object({
  id: z.string(),
  ingredientName: z.string().min(1),
  amount: z.number().min(0),
  unit: z.string(),
  category: z.enum([
    'produce',
    'protein',
    'dairy',
    'grains',
    'canned',
    'spices',
    'condiments',
    'snacks',
    'beverages',
    'frozen',
    'baking',
    'other',
  ]),
  location: z.enum(['pantry', 'fridge', 'freezer', 'counter', 'other']),
  lowStockThreshold: z.number().min(0),
  expiresAt: z.string().optional(),
  notes: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

// Exported for use by the auth-aware data-layer, which runs its own import path so
// writes hit Firestore (or IndexedDB) through the correct routing instead of bypassing
// directly into local storage.
export const ImportSchemas = {
  recipe: RecipeImportSchema,
  cookingHistoryEntry: CookingHistoryEntrySchema,
  mealPlanEntry: MealPlanEntrySchema,
  pantryItem: PantryItemSchema,
  shoppingListItem: ShoppingListItemSchema,
};

// Create custom stores for different data types
const recipeStore = createStore('recipe-sense-recipes', 'recipes');
const sessionStore = createStore('recipe-sense-sessions', 'sessions');
const historyStore = createStore('recipe-sense-history', 'history');
const shoppingStore = createStore('recipe-sense-shopping', 'shopping');
const pantryStore = createStore('recipe-sense-pantry', 'pantry');
const mealPlanStore = createStore('recipe-sense-meal-plans', 'meal-plans');
const settingsStore = createStore('recipe-sense-settings', 'settings');
const metaStore = createStore('recipe-sense-meta', 'meta');

// Keys for settings store
const SETTINGS_KEY = 'app-settings';
const SCHEMA_VERSION_KEY = 'schema-version';
const CURRENT_SESSION_KEY = 'current-session';

// Settings defaults are versioned separately from the IndexedDB SCHEMA_VERSION:
// signed-in users' settings live in Firestore (users/{uid}/meta/settings), which
// runMigrations() below can never reach. migrateSettings() runs at load time in
// ui-store.loadSettings() instead, covering both storage backends.
export const SETTINGS_VERSION = 2;

// Default settings
export const DEFAULT_SETTINGS: AppSettings = {
  theme: 'system',
  reducedMotion: false,
  soundEnabled: true,
  vibrationEnabled: true,
  defaultServings: 4,
  preferredUnits: 'metric',
  timerAlertVolume: 80,
  keepScreenAwake: true,
  autoStartTimers: false,
  settingsVersion: SETTINGS_VERSION,
};

/**
 * One-time default flips for already-stored settings. Stored settings are saved
 * wholesale (no per-key merge on load), so changing DEFAULT_SETTINGS alone only
 * affects fresh installs — existing users need their stored copy bumped.
 */
export function migrateSettings(stored: AppSettings): AppSettings {
  let settings = stored;
  const version = stored.settingsVersion ?? 1;
  if (version < 2) {
    // v2: cooking sessions keep the screen awake by default (opt-out in Settings)
    settings = { ...settings, keepScreenAwake: true };
  }
  return { ...settings, settingsVersion: SETTINGS_VERSION };
}

// ============ Migration Functions ============

async function getStoredSchemaVersion(): Promise<number> {
  const version = await get<number>(SCHEMA_VERSION_KEY, metaStore);
  return version ?? 0;
}

async function setStoredSchemaVersion(version: number): Promise<void> {
  await set(SCHEMA_VERSION_KEY, version, metaStore);
}

async function runMigrations(): Promise<void> {
  const storedVersion = await getStoredSchemaVersion();
  
  if (storedVersion >= SCHEMA_VERSION) {
    return; // No migrations needed
  }
  
  console.log(`Running migrations from version ${storedVersion} to ${SCHEMA_VERSION}`);
  
  // Migration from 0 to 1 (initial setup)
  if (storedVersion < 1) {
    // Fresh install, no migration needed
    console.log('Fresh install detected, setting up initial schema');
  }
  
  // Future migrations would go here:
  // if (storedVersion < 2) { ... }
  
  await setStoredSchemaVersion(SCHEMA_VERSION);
}

// Initialize database and run migrations
export async function initDatabase(): Promise<void> {
  await runMigrations();
  
  // Ensure default settings exist
  const settings = await getSettings();
  if (!settings) {
    await saveSettings(DEFAULT_SETTINGS);
  }
}

// ============ Recipe Operations ============

export async function getAllRecipes(): Promise<Recipe[]> {
  const allKeys = await keys<string>(recipeStore);
  const recipes = await Promise.all(
    allKeys.map(key => get<Recipe>(key, recipeStore))
  );
  return recipes.filter((r): r is Recipe => r !== undefined);
}

export async function getRecipe(id: string): Promise<Recipe | undefined> {
  return get<Recipe>(id, recipeStore);
}

export async function saveRecipe(recipe: Recipe): Promise<void> {
  await set(recipe.id, recipe, recipeStore);
}

export async function deleteRecipe(id: string): Promise<void> {
  await del(id, recipeStore);
}

export async function getRecipeCount(): Promise<number> {
  const allKeys = await keys(recipeStore);
  return allKeys.length;
}

// ============ Cooking Session Operations ============

export async function getCurrentSession(): Promise<CookingSession | undefined> {
  return get<CookingSession>(CURRENT_SESSION_KEY, sessionStore);
}

export async function saveCurrentSession(session: CookingSession | null): Promise<void> {
  if (session) {
    await set(CURRENT_SESSION_KEY, session, sessionStore);
  } else {
    await del(CURRENT_SESSION_KEY, sessionStore);
  }
}

// ============ Cooking History Operations ============

export async function getCookingHistory(): Promise<CookingHistoryEntry[]> {
  const allKeys = await keys<string>(historyStore);
  const entries = await Promise.all(
    allKeys.map(key => get<CookingHistoryEntry>(key, historyStore))
  );
  return entries
    .filter((e): e is CookingHistoryEntry => e !== undefined)
    .sort((a, b) => new Date(b.cookedAt).getTime() - new Date(a.cookedAt).getTime());
}

export async function addCookingHistoryEntry(entry: CookingHistoryEntry): Promise<void> {
  await set(entry.id, entry, historyStore);
}

export async function deleteCookingHistoryEntry(id: string): Promise<void> {
  await del(id, historyStore);
}

export async function clearCookingHistory(): Promise<void> {
  await clear(historyStore);
}

// ============ Shopping List Operations ============

export async function getShoppingList(): Promise<ShoppingListItem[]> {
  const allKeys = await keys<string>(shoppingStore);
  const items = await Promise.all(
    allKeys.map(key => get<ShoppingListItem>(key, shoppingStore))
  );
  return items.filter((i): i is ShoppingListItem => i !== undefined);
}

export async function saveShoppingListItem(item: ShoppingListItem): Promise<void> {
  await set(item.id, item, shoppingStore);
}

export async function deleteShoppingListItem(id: string): Promise<void> {
  await del(id, shoppingStore);
}

export async function clearShoppingList(): Promise<void> {
  await clear(shoppingStore);
}

// ============ Pantry Operations ============

export async function getPantryItems(): Promise<PantryItem[]> {
  const allKeys = await keys<string>(pantryStore);
  const items = await Promise.all(
    allKeys.map((key) => get<PantryItem>(key, pantryStore))
  );
  return items
    .filter((item): item is PantryItem => item !== undefined)
    .sort((a, b) => a.ingredientName.localeCompare(b.ingredientName));
}

export async function savePantryItem(item: PantryItem): Promise<void> {
  await set(item.id, item, pantryStore);
}

export async function deletePantryItem(id: string): Promise<void> {
  await del(id, pantryStore);
}

export async function clearPantryItems(): Promise<void> {
  await clear(pantryStore);
}

// ============ Meal Plan Operations ============

export async function getMealPlanEntries(): Promise<MealPlanEntry[]> {
  const allKeys = await keys<string>(mealPlanStore);
  const entries = await Promise.all(
    allKeys.map(key => get<MealPlanEntry>(key, mealPlanStore))
  );
  return entries
    .filter((entry): entry is MealPlanEntry => entry !== undefined)
    .sort((a, b) => {
      const dateCmp = a.date.localeCompare(b.date);
      if (dateCmp !== 0) return dateCmp;
      return a.mealType.localeCompare(b.mealType);
    });
}

export async function saveMealPlanEntry(entry: MealPlanEntry): Promise<void> {
  await set(entry.id, entry, mealPlanStore);
}

export async function deleteMealPlanEntry(id: string): Promise<void> {
  await del(id, mealPlanStore);
}

export async function clearMealPlanEntries(): Promise<void> {
  await clear(mealPlanStore);
}

// ============ Settings Operations ============

export async function getSettings(): Promise<AppSettings | undefined> {
  return get<AppSettings>(SETTINGS_KEY, settingsStore);
}

export async function saveSettings(settings: AppSettings): Promise<void> {
  await set(SETTINGS_KEY, settings, settingsStore);
}

// ============ Export/Import Operations ============

export async function exportCookbook(): Promise<CookbookExport> {
  const [recipes, cookingHistory, shoppingList, pantryItems, mealPlans, settings] = await Promise.all([
    getAllRecipes(),
    getCookingHistory(),
    getShoppingList(),
    getPantryItems(),
    getMealPlanEntries(),
    getSettings(),
  ]);
  
  return {
    schemaVersion: SCHEMA_VERSION,
    exportedAt: new Date().toISOString(),
    recipes,
    cookingHistory,
    shoppingList,
    pantryItems,
    mealPlans,
    settings: settings ?? DEFAULT_SETTINGS,
  };
}

// NOTE: The canonical importCookbook lives in @/lib/data-layer. It runs the same
// Zod validation (using ImportSchemas exported above) but writes through the
// auth-aware data-layer save functions so authenticated users land in Firestore
// instead of a local-only IndexedDB bypass. This file no longer exports its own
// importCookbook to avoid two divergent implementations.

// ============ Clear All Data ============

export async function clearAllData(): Promise<void> {
  await Promise.all([
    clear(recipeStore),
    clear(sessionStore),
    clear(historyStore),
    clear(shoppingStore),
    clear(pantryStore),
    clear(mealPlanStore),
    // Don't clear settings and meta by default
  ]);
}

export async function clearEverything(): Promise<void> {
  await Promise.all([
    clear(recipeStore),
    clear(sessionStore),
    clear(historyStore),
    clear(shoppingStore),
    clear(pantryStore),
    clear(mealPlanStore),
    clear(settingsStore),
    clear(metaStore),
  ]);
}
